import { useFormContext } from 'react-hook-form';
import { Range, getTrackBackground } from 'react-range';
import './InputSliderRange.scss';

const ACCENT = '#fdba74';
const TRACK = '#e5e7eb';

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const toNumber = (v) => (v === '' || v === undefined || v === null || isNaN(Number(v)) ? null : Number(v));

// Range filter as a two-thumb slider plus two exact-value inputs, stored in the same
// { min, max } shape as InputTextRange (so TableFilters flattens it to <field>Min/<field>Max).
// A thumb left at its end of the scale means "no bound" — the field stays empty, so an
// untouched slider doesn't filter anything; the max end reads "<max>+" for that reason.
// The inputs accept values beyond the scale (the thumb then just pins to the end).
const InputSliderRange = ({ name, min = 0, max = 100, step = 1, unit = '', disabled = false }) => {
    const { watch, setValue } = useFormContext();

    const minValue = toNumber(watch(`${name}.min`));
    const maxValue = toNumber(watch(`${name}.max`));
    const hi = Math.max(max, min + step); // react-range throws if min === max

    const thumbs = [
        clamp(minValue ?? min, min, hi),
        clamp(maxValue ?? hi, min, hi),
    ];
    if (thumbs[0] > thumbs[1]) thumbs[0] = thumbs[1];

    const set = (bound, value) => setValue(`${name}.${bound}`, value, { shouldDirty: true });

    const handleSlide = ([lo, up]) => {
        set('min', lo <= min ? '' : String(lo));
        set('max', up >= hi ? '' : String(up));
    };

    // On blur, swap the bounds if they were typed the wrong way round.
    const normalize = () => {
        if (minValue !== null && maxValue !== null && minValue > maxValue) {
            set('min', String(maxValue));
            set('max', String(minValue));
        }
    };

    const suffix = unit ? ` ${unit}` : '';
    const format = (v) => new Intl.NumberFormat('fr-MA', { maximumFractionDigits: 2 }).format(v);

    return (
        <div className={`slider-range${disabled ? ' slider-range--disabled' : ''}`}>
            <Range
                values={thumbs}
                step={step}
                min={min}
                max={hi}
                disabled={disabled}
                onChange={handleSlide}
                renderTrack={({ props, children }) => (
                    <div
                        onMouseDown={props.onMouseDown}
                        onTouchStart={props.onTouchStart}
                        className="slider-range__track-wrap"
                    >
                        <div
                            ref={props.ref}
                            className="slider-range__track"
                            style={{
                                background: getTrackBackground({ values: thumbs, colors: [TRACK, ACCENT, TRACK], min, max: hi }),
                            }}
                        >
                            {children}
                        </div>
                    </div>
                )}
                renderThumb={({ props, isDragged }) => {
                    const { key, ...thumbProps } = props;
                    return (
                        <div key={key} {...thumbProps} className={`slider-range__thumb${isDragged ? ' slider-range__thumb--dragged' : ''}`} />
                    );
                }}
            />

            <div className="slider-range__inputs">
                <input
                    type="number"
                    inputMode="decimal"
                    className="form-control"
                    id={`${name}.min`}
                    placeholder={`${format(min)}${suffix}`}
                    value={watch(`${name}.min`) ?? ''}
                    min={0}
                    onChange={(e) => set('min', e.target.value)}
                    onBlur={normalize}
                    disabled={disabled}
                />
                <input
                    type="number"
                    inputMode="decimal"
                    className="form-control"
                    placeholder={`${format(hi)}+${suffix}`}
                    value={watch(`${name}.max`) ?? ''}
                    min={0}
                    onChange={(e) => set('max', e.target.value)}
                    onBlur={normalize}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

InputSliderRange.isRangeFilter = true;

export default InputSliderRange;
